import React from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "../../utils/cn";

export interface ActionCardProps {
  icon: React.ReactNode;
  iconClassName?: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  badge?: React.ReactNode;
  footer?: React.ReactNode;
  onClick?: () => void;
  layout?: "card" | "row";
  className?: string;
}

const ActionCard: React.FC<ActionCardProps> = ({
  icon,
  iconClassName = "bg-[var(--color-primary-100)] text-[var(--color-primary-700)]",
  title,
  description,
  badge,
  footer,
  onClick,
  layout = "card",
  className = "",
}) => {
  const clickable = typeof onClick === "function";

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!clickable) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.();
    }
  };

  const interactiveClasses = clickable
    ? "cursor-pointer transition-all duration-200 hover:border-[var(--color-primary-300)] hover:shadow-[var(--shadow-md)] hover:-translate-y-1 focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] focus-visible:outline-none"
    : "";

  if (layout === "row") {
    return (
      <div
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
        onClick={onClick}
        onKeyDown={clickable ? handleKeyDown : undefined}
        className={cn(
          "group flex items-center gap-3.5 bg-[var(--color-bg-base)] border border-[var(--color-border-primary)]",
          "rounded-card p-[18px] shadow-[var(--shadow-sm)]",
          interactiveClasses,
          className
        )}
      >
        <span className={`w-[46px] h-[46px] rounded-[13px] flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105 duration-200 ${iconClassName}`}>
          {icon}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-body-md font-bold text-[var(--color-text-primary)] group-hover:text-[var(--color-primary-600)] transition-colors truncate">
            {title}
          </span>
          {description && (
            <span className="block text-body-xs text-[var(--color-text-tertiary)] mt-0.5 truncate">
              {description}
            </span>
          )}
        </span>
        <ChevronRight className="w-[17px] h-[17px] text-[var(--color-layout-header-icon-secondary)] flex-none" />
      </div>
    );
  }

  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={clickable ? handleKeyDown : undefined}
      className={cn(
        "group relative bg-[var(--color-bg-base)] border border-[var(--color-border-primary)]",
        "rounded-card p-4 sm:p-5 shadow-[var(--shadow-sm)]",
        "flex flex-col justify-between",
        clickable &&
          "cursor-pointer transition-all duration-200 hover:border-[var(--color-primary-300)] hover:shadow-[var(--shadow-md)] hover:-translate-y-1 focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] focus-visible:outline-none",
        className
      )}
    >
      <div>
        <div className="flex items-center justify-between gap-2.5 mb-3">
          <span className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105 duration-200 ${iconClassName}`}>
            {icon}
          </span>
          {badge}
        </div>

        <h3 className="text-body-md font-bold text-[var(--color-text-primary)] group-hover:text-[var(--color-primary-600)] transition-colors leading-snug">
          {title}
        </h3>

        {description && (
          <p className="text-body-xs text-[var(--color-text-secondary)] mt-1 line-clamp-2 leading-relaxed">
            {description}
          </p>
        )}
      </div>

      {footer && (
        <div className="mt-4 pt-2.5 border-t border-[var(--color-border-tertiary)]">{footer}</div>
      )}
    </div>
  );
};

export default ActionCard;
